/* eslint-disable */

import logoFullDark from "assets/svg/logo_full_dark.svg";
import logoFullLight from "assets/svg/logo_full_light.svg";
import logoIcon from "assets/svg/logo_icon.svg";
import ServerProgressPanel from "components/serverProgress/ServerProgressPanel";
import { Box, Button, Dropdown, DropdownItem, Flex, Text } from "components/ui";
import { useTheme } from "hooks/useTheme";
import { useTranslation } from "hooks/useTranslation";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import {
  MdBusiness,
  MdDarkMode,
  MdHelpOutline,
  MdLightMode,
  MdLiveHelp,
  MdLogout,
  MdPeople,
  MdPerson,
  MdSettings,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { authService } from "services/authService";
import { getThemeColor } from "theme/colors";
import Links from "./components/Links";

import routes from "routes";

const Sidebar = (props: {
  open: boolean;
  onClose: React.MouseEventHandler<HTMLSpanElement>;
  onToggle?: () => void;
}) => {
  const { open, onClose, onToggle } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark, setThemeMode } = useTheme();

  const handleLogout = () => {
    authService.logout();
    navigate("/auth/sign-in");
  };

  const handleToggleTheme = () => {
    setThemeMode(isDark ? "light" : "dark");
  };

  const handleOpenDocs = () => {
    window.open(
      "https://www.strikeup.io/documentation",
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <Box
      position="fixed"
      zIndex={{ base: 50, xl: 0 }}
      display="flex"
      h="100vh"
      w={open ? "240px" : "64px"}
      flexDirection="column"
      bg={getThemeColor("sidebar.bg")}
      borderRight="1px solid"
      borderColor={getThemeColor("sidebar.divider")}
      transition="all 0.175s linear"
      transform="translateX(0)"
      overflowY="auto"
      className="thin-scrollbar"
    >
      <Flex
        w="full"
        h={16}
        align="center"
        justify={open ? "space-between" : "center"}
        px={open ? 4 : 2}
        borderBottom="1px solid"
        borderColor={getThemeColor("sidebar.divider")}
      >
        {open ? (
          <Box flex={1} display="flex" justifyContent="center">
            <img
              src={isDark ? logoFullLight : logoFullDark}
              alt="strikeUp"
              style={{ height: "22px", width: "auto" }}
            />
          </Box>
        ) : (
          <Box
            as="button"
            onClick={onToggle || (onClose as any)}
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            bg="transparent"
            border="none"
            p={0}
          >
            <img
              src={logoIcon}
              alt="strikeUp"
              style={{ height: "28px", width: "auto" }}
            />
          </Box>
        )}

        {/* Collapse button — only shown when expanded */}
        {open && (
          <Button
            onClick={onToggle || (onClose as any)}
            variant="ghost"
            size="sm"
            minW="auto"
            px={2}
          >
            <HiChevronLeft size={20} />
          </Button>
        )}
      </Flex>

      {/* Nav item */}
      <Box as="ul" flex={1} pt={5} overflowY="auto" className="thin-scrollbar">
        <Links routes={routes} collapsed={!open} />
      </Box>

      {/* Settings Menu */}
      <Box
        px={open ? 4 : 2}
        py={3}
        borderTop="1px solid"
        borderColor={getThemeColor("sidebar.divider")}
      >
        <Dropdown
          positioning={open ? "top-start" : "right-start"}
          trigger={
            <Button
              variant="ghost"
              size="sm"
              width="full"
              justifyContent={open ? "flex-start" : "center"}
              px={open ? 2 : 1}
              py={1}
              minH="auto"
              h="auto"
              _hover={{ bg: getThemeColor("sidebar.hoverBg") }}
            >
              <MdSettings size={18} />
              {open && (
                <Text ml={2} fontSize="sm">
                  {t("app.sidebar.settings")}
                </Text>
              )}
            </Button>
          }
        >
          <DropdownItem onClick={() => navigate("/admin/profile")}>
            <Box display="flex" alignItems="center" cursor="pointer">
              <MdPerson size={18} style={{ marginRight: "8px" }} />
              {t("app.sidebar.profile")}
            </Box>
          </DropdownItem>
          <DropdownItem onClick={() => navigate("/admin/company")}>
            <Box display="flex" alignItems="center" cursor="pointer">
              <MdBusiness size={18} style={{ marginRight: "8px" }} />
              {t("app.sidebar.company")}
            </Box>
          </DropdownItem>
          <DropdownItem onClick={() => navigate("/admin/users")}>
            <Box display="flex" alignItems="center" cursor="pointer">
              <MdPeople size={18} style={{ marginRight: "8px" }} />
              {t("app.sidebar.users")}
            </Box>
          </DropdownItem>
          <Box h="1px" bg={getThemeColor("border.default")} my={1} />
          <DropdownItem onClick={handleToggleTheme}>
            <Box display="flex" alignItems="center" cursor="pointer">
              {isDark ? (
                <MdLightMode size={18} style={{ marginRight: "8px" }} />
              ) : (
                <MdDarkMode size={18} style={{ marginRight: "8px" }} />
              )}
              {isDark ? t("app.sidebar.lightMode") : t("app.sidebar.darkMode")}
            </Box>
          </DropdownItem>
          <DropdownItem onClick={handleOpenDocs}>
            <Box display="flex" alignItems="center" cursor="pointer">
              <MdHelpOutline size={18} style={{ marginRight: "8px" }} />
              {t("app.sidebar.docs")}
            </Box>
          </DropdownItem>
          <DropdownItem onClick={() => navigate("/admin/help")}>
            <Box display="flex" alignItems="center" cursor="pointer">
              <MdLiveHelp size={18} style={{ marginRight: "8px" }} />
              {t("app.sidebar.faq")}
            </Box>
          </DropdownItem>
          <DropdownItem onClick={handleLogout}>
            <Box display="flex" alignItems="center" cursor="pointer">
              <MdLogout size={18} style={{ marginRight: "8px" }} />
              <Text color="red.500">{t("app.sidebar.logout")}</Text>
            </Box>
          </DropdownItem>
        </Dropdown>
      </Box>

      {/* Server Progress Panel at bottom of sidebar */}
      <Box>
        <ServerProgressPanel />
      </Box>
    </Box>
  );
};

export default Sidebar;
